import React, { useState, useRef, useEffect, useCallback, forwardRef } from "react";
import ReactDOM from "react-dom";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

// maxLocal = digits after the dial code (local number length)
const COUNTRIES = [
  { code: "EG", dial: "20",  maxLocal: 10 },
  { code: "SA", dial: "966", maxLocal: 9  },
  { code: "AE", dial: "971", maxLocal: 9  },
  { code: "KW", dial: "965", maxLocal: 8  },
  { code: "QA", dial: "974", maxLocal: 8  },
  { code: "BH", dial: "973", maxLocal: 8  },
  { code: "OM", dial: "968", maxLocal: 8  },
  { code: "JO", dial: "962", maxLocal: 9  },
  { code: "LB", dial: "961", maxLocal: 8  },
  { code: "IQ", dial: "964", maxLocal: 10 },
  { code: "SY", dial: "963", maxLocal: 9  },
  { code: "PS", dial: "970", maxLocal: 9  },
  { code: "MA", dial: "212", maxLocal: 9  },
  { code: "TN", dial: "216", maxLocal: 8  },
  { code: "DZ", dial: "213", maxLocal: 9  },
  { code: "LY", dial: "218", maxLocal: 9  },
  { code: "SD", dial: "249", maxLocal: 9  },
  { code: "TR", dial: "90",  maxLocal: 10 },
  { code: "US", dial: "1",   maxLocal: 10 },
  { code: "GB", dial: "44",  maxLocal: 10 },
  { code: "DE", dial: "49",  maxLocal: 11 },
  { code: "FR", dial: "33",  maxLocal: 9  },
  { code: "IT", dial: "39",  maxLocal: 10 },
  { code: "ES", dial: "34",  maxLocal: 9  },
  { code: "NL", dial: "31",  maxLocal: 9  },
  { code: "BE", dial: "32",  maxLocal: 9  },
  { code: "CH", dial: "41",  maxLocal: 9  },
  { code: "SE", dial: "46",  maxLocal: 9  },
  { code: "NO", dial: "47",  maxLocal: 8  },
  { code: "DK", dial: "45",  maxLocal: 8  },
  { code: "PL", dial: "48",  maxLocal: 9  },
  { code: "RU", dial: "7",   maxLocal: 10 },
  { code: "AU", dial: "61",  maxLocal: 9  },
  { code: "NZ", dial: "64",  maxLocal: 9  },
  { code: "IN", dial: "91",  maxLocal: 10 },
  { code: "PK", dial: "92",  maxLocal: 10 },
  { code: "BD", dial: "880", maxLocal: 10 },
  { code: "CN", dial: "86",  maxLocal: 11 },
  { code: "JP", dial: "81",  maxLocal: 10 },
  { code: "KR", dial: "82",  maxLocal: 10 },
  { code: "ID", dial: "62",  maxLocal: 11 },
  { code: "MY", dial: "60",  maxLocal: 9  },
  { code: "SG", dial: "65",  maxLocal: 8  },
  { code: "PH", dial: "63",  maxLocal: 10 },
  { code: "NG", dial: "234", maxLocal: 10 },
  { code: "ZA", dial: "27",  maxLocal: 9  },
  { code: "GH", dial: "233", maxLocal: 9  },
  { code: "KE", dial: "254", maxLocal: 9  },
  { code: "ET", dial: "251", maxLocal: 9  },
  { code: "BR", dial: "55",  maxLocal: 11 },
  { code: "MX", dial: "52",  maxLocal: 10 },
  { code: "AR", dial: "54",  maxLocal: 10 },
];

// Countries whose local number genuinely starts with 0
const KEEP_LEADING_ZERO = ["IT"];

// Convert Arabic-Indic / Persian digits to Latin digits
const normalizeDigits = (str) =>
  str
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 1632))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 1776));

const Flag = ({ code }) => (
  <img
    src={`https://flagcdn.com/w20/${code.toLowerCase()}.png`}
    srcSet={`https://flagcdn.com/w40/${code.toLowerCase()}.png 2x`}
    width={20} height={15} alt={code}
    style={{ objectFit: "cover", borderRadius: 2, flexShrink: 0, display: "inline-block" }}
    onError={(e) => { e.target.style.display = "none"; }}
  />
);

// Dial code is always LTR so "+20" never flips to "20+" inside RTL text
const Dial = ({ dial, style }) => (
  <span dir="ltr" style={{ unicodeBidi: "isolate", ...style }}>+{dial}</span>
);

const parsePhone = (value) => {
  if (!value) return { dial: "20", number: "" };
  const digits = value.startsWith("+") ? value.slice(1) : value;
  const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  const match = sorted.find((c) => digits.startsWith(c.dial));
  if (match) return { dial: match.dial, number: digits.slice(match.dial.length) };
  return { dial: "20", number: digits };
};

const PhoneField = forwardRef(function PhoneField(
  { value = "", onChange, disabled, error, label, required, focusColor = "var(--accent-color)" },
  inputRef
) {
  const { t, i18n } = useTranslation();
  const pageDir = i18n.dir(); // only used for label / error / dropdown list

  const { dial, number } = parsePhone(value);
  const [selectedDial, setSelectedDial] = useState(dial);
  const [localNumber, setLocalNumber] = useState(number);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState({});
  const btnRef = useRef(null);
  const searchRef = useRef(null);

  // Sync external value changes
  useEffect(() => {
    const p = parsePhone(value);
    setSelectedDial(p.dial);
    setLocalNumber(p.number);
  }, [value]);

  const selectedCountry = COUNTRIES.find((c) => c.dial === selectedDial) || COUNTRIES[0];
  const countryName = (c) => t(`countries.${c.code}`);

  // The selector is always on the left, so the dropdown is always anchored to the left
  const updatePos = useCallback(() => {
    if (!btnRef.current) return;
    const width = 260;
    const r = btnRef.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    setDropdownStyle({ position: "fixed", top: r.bottom + 4, left, width });
  }, []);

  // Follow the button on scroll / resize while open
  useEffect(() => {
    if (!open) return;
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open, updatePos]);

  // Auto-focus search
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 10);
  }, [open]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const dd = document.getElementById("phone-dd");
      if (btnRef.current && !btnRef.current.contains(e.target) && dd && !dd.contains(e.target)) {
        setOpen(false);
      }
    };
    const keyHandler = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", keyHandler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", keyHandler);
    };
  }, [open]);

  const handleToggle = () => {
    if (disabled) return;
    setSearch("");
    setOpen((o) => !o);
  };

  const handleSelect = (c) => {
    setSelectedDial(c.dial);
    const trimmed = localNumber.slice(0, c.maxLocal);
    setLocalNumber(trimmed);
    onChange("+" + c.dial + trimmed);
    setOpen(false);
    setSearch("");
  };

  const handleNumberChange = (e) => {
    let raw = normalizeDigits(e.target.value).replace(/\D/g, "");
    if (!KEEP_LEADING_ZERO.includes(selectedCountry.code)) raw = raw.replace(/^0+/, "");
    raw = raw.slice(0, selectedCountry.maxLocal);
    setLocalNumber(raw);
    onChange("+" + selectedDial + raw);
  };

  const q = normalizeDigits(search).trim().toLowerCase();
  const filtered = COUNTRIES.filter(
    (c) =>
      countryName(c).toLowerCase().includes(q) ||
      c.code.toLowerCase().includes(q) ||
      c.dial.includes(q.replace("+", ""))
  );

  const dropdown =
    open &&
    ReactDOM.createPortal(
      <div
        id="phone-dd"
        dir={pageDir}
        style={{
          ...dropdownStyle,
          maxHeight: 280, overflowY: "auto",
          background: "white",
          border: "1px solid #ddd",
          borderRadius: 8,
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          zIndex: 99999,
        }}
      >
        <div style={{ padding: 8, borderBottom: "1px solid #eee", position: "sticky", top: 0, background: "white" }}>
          <input
            ref={searchRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("phone_field.search_placeholder")}
            style={{
              width: "100%", padding: "6px 10px", borderRadius: 6,
              border: "1px solid #ddd", fontSize: 13, outline: "none",
              boxSizing: "border-box", textAlign: "start",
            }}
          />
        </div>

        {filtered.map((c) => (
          <div
            key={c.code}
            onMouseDown={(e) => { e.preventDefault(); handleSelect(c); }}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 12px", cursor: "pointer",
              background: c.dial === selectedDial ? "#f3e5f5" : "transparent",
            }}
            onMouseEnter={(e) => { if (c.dial !== selectedDial) e.currentTarget.style.background = "#f9f2ff"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = c.dial === selectedDial ? "#f3e5f5" : "transparent"; }}
          >
            <Flag code={c.code} />
            <Dial dial={c.dial} style={{ fontSize: 12, color: "#555", minWidth: 40, fontWeight: 600 }} />
            <span style={{ fontSize: 13, color: "#222" }}>{countryName(c)}</span>
            <span style={{ fontSize: 11, color: "#aaa", marginInlineStart: "auto" }}>
              {t("phone_field.digits", { count: c.maxLocal })}
            </span>
          </div>
        ))}

        {filtered.length === 0 && (
          <div style={{ padding: 12, textAlign: "center", color: "#999", fontSize: 13 }}>
            {t("phone_field.no_results")}
          </div>
        )}
      </div>,
      document.body
    );

  return (
    <Box dir={pageDir}>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontWeight: 500, textAlign: "start" }}>
        {label} {required && <span className="text-danger">*</span>}
      </Typography>

      {/* Always LTR: country code on the left, number on the right in every language */}
      <Box
        dir="ltr"
        display="flex"
        sx={{
          border: error ? "1px solid #d32f2f" : "1px solid rgba(0,0,0,0.23)",
          borderRadius: "8px",
          background: disabled ? "#f5f5f5" : "white",
          "&:focus-within": {
            borderColor: error ? "#d32f2f" : focusColor,
            boxShadow: `0 0 0 1px ${error ? "#d32f2f" : focusColor}`,
          },
        }}
      >
        <Box
          ref={btnRef}
          onClick={handleToggle}
          sx={{
            display: "flex", alignItems: "center", gap: 0.7,
            px: 1.2, py: "8px",
            cursor: disabled ? "default" : "pointer",
            borderRight: "1px solid rgba(0,0,0,0.12)",
            background: disabled ? "#efefef" : open ? "#f0e8fb" : "#f7f7f7",
            borderRadius: "8px 0 0 8px",
            userSelect: "none", flexShrink: 0, minWidth: 86,
            "&:hover": disabled ? {} : { background: "#f0e8fb" },
          }}
        >
          <Flag code={selectedCountry.code} />
          <Dial dial={selectedDial} style={{ fontSize: 13, color: "#333", fontWeight: 600 }} />
          <span style={{ fontSize: 9, color: "#888" }}>{open ? "▲" : "▼"}</span>
        </Box>

        <input
          ref={inputRef}
          type="tel"
          inputMode="numeric"
          value={localNumber}
          onChange={handleNumberChange}
          disabled={disabled}
          aria-invalid={!!error}
          placeholder={"X".repeat(selectedCountry.maxLocal)}
          maxLength={selectedCountry.maxLocal + 2} // headroom for leading zero / paste
          style={{
            flex: 1, border: "none", outline: "none",
            padding: "8px 12px", fontSize: "0.875rem",
            background: "transparent",
            color: disabled ? "#9e9e9e" : "inherit",
            textAlign: "left",
            borderRadius: "0 8px 8px 0", minWidth: 0,
          }}
        />
      </Box>

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block", textAlign: "start" }}>
          {error}
        </Typography>
      )}

      {dropdown}
    </Box>
  );
});

export default PhoneField;